import React, { useCallback, useEffect, useMemo, useState } from 'react'
import axios from 'axios'
import { useSelector } from 'react-redux'
import {
  MdOutlineReplay,
  MdSearch
} from 'react-icons/md'
import { FaMoneyBillTransfer } from 'react-icons/fa6'
import Navbar from './Navbar'

export default function Disbursements() {
  const { url } = useSelector(state => state)

  const [pending, setPending] = useState([])
  const [history, setHistory] = useState([])
  const [loading, setLoading] = useState(true)
  const [disbursingId, setDisbursingId] = useState(null)
  const [error, setError] = useState('')
  const [searchQuery, setSearchQuery] = useState('')

  const formatCurrency = amount =>
    `₦${Number(amount || 0).toLocaleString()}`

  const formatDate = value => {
    if (!value) return '—'

    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return '—'

    return date.toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const fetchDisbursements = useCallback(async () => {
    try {
      setError('')

      const headers = {
        Authorization: `Bearer ${localStorage.getItem('access_token')}`
      }

      const [pendingResponse, historyResponse] = await Promise.all([
        axios.get(
          `${url}/admin/loan-disbursements/pending`,
          { headers }
        ),
        axios.get(
          `${url}/admin/loan-disbursements`,
          { headers }
        )
      ])

      setPending(pendingResponse.data.applications || [])
      setHistory(historyResponse.data.disbursements || [])
    } catch (err) {
      console.error(err)
      setError(
        err.response?.data?.detail ||
        'Unable to load loan disbursements.'
      )
    } finally {
      setLoading(false)
    }
  }, [url])

  useEffect(() => {
    fetchDisbursements()
  }, [fetchDisbursements])

  useEffect(() => {
    const hasProcessingPayout = history.some(
      disbursement => disbursement.status === 'processing'
    )

    if (!hasProcessingPayout) return undefined

    const interval = setInterval(fetchDisbursements, 5000)
    return () => clearInterval(interval)
  }, [history, fetchDisbursements])

  const disburseLoan = async application => {
    const confirmed = window.confirm(
      `Disburse ${formatCurrency(application.rent_amount)} to ${application.landlord_account_name}?`
    )

    if (!confirmed) return

    try {
      setDisbursingId(application.loan_application_id)
      setError('')

      await axios.post(
        `${url}/admin/loan-applications/${application.loan_application_id}/disburse`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`
          }
        }
      )

      await fetchDisbursements()
    } catch (err) {
      console.error(err)
      setError(
        err.response?.data?.detail ||
        'Unable to initiate disbursement.'
      )
    } finally {
      setDisbursingId(null)
    }
  }

  const retryDisbursement = async disbursement => {
    const confirmed = window.confirm(
      `Retry ${formatCurrency(disbursement.amount)} payout to ${disbursement.landlord_account_name}?`
    )

    if (!confirmed) return

    try {
      setDisbursingId(disbursement.loan_application_id)
      setError('')

      await axios.post(
        `${url}/admin/loan-applications/${disbursement.loan_application_id}/disburse`,
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('access_token')}`
          }
        }
      )

      await fetchDisbursements()
    } catch (err) {
      console.error(err)
      setError(
        err.response?.data?.detail ||
        'Unable to retry disbursement.'
      )
    } finally {
      setDisbursingId(null)
    }
  }

  const filteredHistory = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    if (!query) return history

    return history.filter(item =>
      [
        item.email,
        item.reference,
        item.landlord_account_name,
        item.landlord_account_number,
        item.status
      ].some(value => String(value || '').toLowerCase().includes(query))
    )
  }, [history, searchQuery])

  const statusStyle = status => {
    const styles = {
      successful: 'border-green-100 bg-green-50 text-green-700',
      failed: 'border-red-100 bg-red-50 text-red-700',
      processing: 'border-amber-100 bg-amber-50 text-amber-700',
      unknown: 'border-orange-100 bg-orange-50 text-orange-700'
    }

    return styles[status] || 'border-gray-200 bg-gray-50 text-gray-600'
  }

  return (
    <div className="flex min-h-screen w-full bg-[#F7F9FC] font-[Satoshi]">
      <Navbar />

      <main className="min-w-0 flex-1 px-5 pb-10 pt-24 sm:px-6 lg:px-8 lg:pt-8">
        <div className="mx-auto max-w-[1500px]">
          <div className="mb-7 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-sm font-semibold text-green-600">PAYOUTS</p>
              <h1 className="mt-1 text-3xl font-bold tracking-tight text-gray-900">Loan disbursements</h1>
              <p className="mt-2 text-sm text-gray-500">
                Pay approved annual rent directly to landlord bank accounts.
              </p>
            </div>

            <div className="w-fit rounded-full border border-violet-100 bg-violet-50 px-3.5 py-2 text-xs font-semibold text-violet-700">
              {pending.length} awaiting payout
            </div>
          </div>

          {error && (
            <div className="mb-6 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-medium text-red-600">
              {error}
            </div>
          )}

          {loading ? (
            <div className="space-y-4">
              <div className="h-40 animate-pulse rounded-2xl border border-gray-200 bg-white" />
              <div className="h-80 animate-pulse rounded-2xl border border-gray-200 bg-white" />
            </div>
          ) : (
            <>
              <section className="mb-8">
                <div className="mb-4 flex items-center justify-between gap-4">
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">Awaiting disbursement</h2>
                    <p className="mt-1 text-sm text-gray-500">
                      Approved applications waiting for landlord payment.
                    </p>
                  </div>
                </div>

                {pending.length === 0 ? (
                  <div className="rounded-2xl border border-dashed border-gray-300 bg-white px-6 py-12 text-center">
                    <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-green-50 text-green-600">
                      <FaMoneyBillTransfer className="text-xl" />
                    </div>
                    <p className="mt-4 font-semibold text-gray-700">No payouts waiting</p>
                    <p className="mt-1 text-sm text-gray-400">Approved loans will appear here.</p>
                  </div>
                ) : (
                  <div className="grid gap-4 xl:grid-cols-2">
                    {pending.map(application => (
                      <article
                        key={application.loan_application_id}
                        className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm sm:p-6"
                      >
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                          <div className="min-w-0">
                            <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">
                              Application #{application.loan_application_id}
                            </p>
                            <p className="mt-1 break-all font-bold text-gray-900">{application.email}</p>
                            <p className="mt-1 text-sm text-gray-500">{application.property_address || '—'}</p>
                          </div>

                          <div className="rounded-xl bg-green-50 px-4 py-3 sm:text-right">
                            <p className="text-xs font-semibold text-green-700">Payout amount</p>
                            <p className="mt-1 text-xl font-bold text-gray-900">
                              {formatCurrency(application.rent_amount)}
                            </p>
                          </div>
                        </div>

                        <div className="mt-5 rounded-xl border border-gray-100 bg-gray-50 p-4">
                          <Detail label="Landlord" value={application.landlord_account_name} />
                          <Detail label="Bank code" value={application.landlord_account_bank} />
                          <Detail label="Account number" value={application.landlord_account_number} mono />
                        </div>

                        <button
                          type="button"
                          disabled={disbursingId === application.loan_application_id}
                          onClick={() => disburseLoan(application)}
                          className="mt-5 flex h-11 w-full items-center justify-center gap-2 rounded-xl bg-green-600 px-5 text-sm font-semibold text-white transition hover:bg-green-700 disabled:cursor-not-allowed disabled:bg-gray-300"
                        >
                          <FaMoneyBillTransfer />
                          {disbursingId === application.loan_application_id
                            ? 'Initiating transfer...'
                            : `Disburse ${formatCurrency(application.rent_amount)}`}
                        </button>
                      </article>
                    ))}
                  </div>
                )}
              </section>

              <section>
                <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">Disbursement history</h2>
                    <p className="mt-1 text-sm text-gray-500">Track transfer status and retry failed landlord payouts.</p>
                  </div>

                  <div className="relative w-full lg:max-w-sm">
                    <MdSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-xl text-gray-400" />
                    <input
                      value={searchQuery}
                      onChange={event => setSearchQuery(event.target.value)}
                      placeholder="Search payouts..."
                      className="h-11 w-full rounded-xl border border-gray-300 bg-white pl-11 pr-4 text-sm outline-none transition focus:border-green-500 focus:ring-4 focus:ring-green-100"
                    />
                  </div>
                </div>

                <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm">
                  <div className="overflow-x-auto">
                    <table className="min-w-[1150px] w-full text-left text-sm">
                      <thead className="bg-gray-50 text-[11px] font-bold uppercase tracking-wider text-gray-500">
                        <tr>
                          <th className="px-5 py-4">Customer</th>
                          <th className="px-5 py-4">Landlord</th>
                          <th className="px-5 py-4">Amount</th>
                          <th className="px-5 py-4">Reference</th>
                          <th className="px-5 py-4">Status</th>
                          <th className="px-5 py-4">Completed</th>
                          <th className="px-5 py-4">Action</th>
                        </tr>
                      </thead>

                      <tbody>
                        {filteredHistory.length > 0 ? (
                          filteredHistory.map(disbursement => (
                            <tr key={disbursement.id || disbursement.reference} className="border-t border-gray-100 hover:bg-gray-50/80">
                              <td className="px-5 py-4">
                                <p className="font-semibold text-gray-900">{disbursement.email || '—'}</p>
                                <p className="mt-1 text-xs text-gray-400">Loan #{disbursement.loan_application_id || '—'}</p>
                              </td>
                              <td className="px-5 py-4">
                                <p className="font-medium text-gray-700">{disbursement.landlord_account_name || '—'}</p>
                                <p className="mt-1 font-mono text-xs text-gray-400">{disbursement.landlord_account_number || '—'}</p>
                              </td>
                              <td className="px-5 py-4 font-bold text-gray-900">{formatCurrency(disbursement.amount)}</td>
                              <td className="px-5 py-4 font-mono text-xs text-gray-500">{disbursement.reference || '—'}</td>
                              <td className="px-5 py-4">
                                <span className={`inline-flex rounded-full border px-2.5 py-1 text-[11px] font-bold uppercase ${statusStyle(disbursement.status)}`}>
                                  {disbursement.status || 'unknown'}
                                </span>
                              </td>
                              <td className="px-5 py-4 text-gray-600">{formatDate(disbursement.completed_at)}</td>
                              <td className="px-5 py-4">
                                {disbursement.status === 'failed' ? (
                                  <button
                                    type="button"
                                    onClick={() => retryDisbursement(disbursement)}
                                    disabled={disbursingId === disbursement.loan_application_id}
                                    className="inline-flex h-9 items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 text-xs font-semibold text-red-600 transition hover:bg-red-100 disabled:opacity-50"
                                  >
                                    <MdOutlineReplay className="text-base" />
                                    {disbursingId === disbursement.loan_application_id ? 'Retrying...' : 'Retry'}
                                  </button>
                                ) : (
                                  <span className="text-gray-300">—</span>
                                )}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="7" className="px-6 py-14 text-center text-sm text-gray-400">
                              No disbursements found.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </section>
            </>
          )}
        </div>
      </main>
    </div>
  )
}

function Detail({ label, value, mono = false }) {
  return (
    <div className="flex items-start justify-between gap-6 border-b border-gray-200 py-2.5 last:border-0 last:pb-0 first:pt-0">
      <span className="text-sm text-gray-400">{label}</span>
      <span className={`text-right text-sm font-semibold text-gray-800 ${mono ? 'font-mono' : ''}`}>
        {value || '—'}
      </span>
    </div>
  )
}
